import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import ChevronRight from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ImageCarousel from "@/components/ImageCarousel";
import { useCampground } from "@/contexts/AppConfigContext";
import { useCurrency } from "@/hooks/useCurrency";
import type { AvailabilityCampsite, Campsite, CampsiteImage, TopAttribute } from "@/types/api";
import { formatCurrency } from "@/utils/pricing";

interface CampsiteDetailDialogProps {
  open: boolean;
  onClose: () => void;
  campsite: Campsite;
  isAvailable: boolean;
  images: CampsiteImage[];
  onSelect: (campsite: Campsite) => void;
  priceInfo: AvailabilityCampsite["prices"];
  nights: number;
  guests: number;
  showAlternativeDates?: boolean;
  onShowAlternativeDates?: () => void;
}

function renderTopAttributes(attributes: TopAttribute[]) {
  return attributes.map((attribute) => (
    <Chip
      key={attribute.name}
      label={attribute.name}
      icon={
        attribute.icon_svg ? (
          <Box
            sx={{
              width: 16,
              height: 16,
              display: "flex",
              "& svg": {
                width: "100%",
                height: "100%",
              },
            }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG icon from trusted backend API
            dangerouslySetInnerHTML={{ __html: attribute.icon_svg }}
          />
        ) : undefined
      }
      sx={{
        backgroundColor: "grey.200",
        color: "text.secondary",
      }}
    />
  ));
}

export default function CampsiteDetailDialog({
  open,
  onClose,
  campsite,
  isAvailable,
  images,
  onSelect,
  priceInfo,
  nights,
  guests,
  showAlternativeDates,
  onShowAlternativeDates,
}: CampsiteDetailDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const { data: campgroundData } = useCampground();
  const { currencyCode } = useCurrency();
  const { i18n } = useLingui();

  const topAttributes = [
    campsite.top1_attribute,
    campsite.top2_attribute,
    campsite.top3_attribute,
  ].filter((attribute) => attribute !== null);
  const plainTextDescription = campsite.description.replace(/<[^>]*>/g, "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      slotProps={{ paper: { sx: { maxWidth: 480, width: "100%" } } }}
    >
      <DialogTitle>
        {campsite.name}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 0, md: 2 } }}>
        <Stack spacing={2}>
          {/* Image carousel */}
          {images.length > 0 && (
            <Box
              sx={{
                width: "100%",
                aspectRatio: "4 / 3",
                overflow: "hidden",
                borderRadius: { xs: 0, md: 2 },
                bgcolor: "grey.300",
              }}
            >
              <ImageCarousel images={images} variant="4_3_big" opacity={1} />
            </Box>
          )}
          <Stack spacing={2} px={{ xs: 2, md: 0 }}>
            {/* Attributes section */}
            {topAttributes.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {renderTopAttributes(topAttributes)}
              </Stack>
            )}
            {/* Description */}
            <Typography variant="body1">{plainTextDescription}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          p: { xs: 0, md: 3 },
          pt: 0,
          borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
        }}
      >
        {isAvailable ? (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="flex-end"
            width="100%"
            sx={{
              // borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
              p: { xs: 2, md: 0 },
              pt: 2,
            }}
          >
            <Stack spacing={0.5} alignItems="flex-end">
              <Typography variant="body2" color="text.secondary">
                <Trans>
                  Price for {nights} <Plural value={nights} one="night" other="nights" />, {guests}{" "}
                  <Plural value={guests} one="guest" other="guests" />
                </Trans>
              </Typography>
              {priceInfo?.total && (
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(priceInfo.total, campgroundData.campground.currency, i18n.locale)}
                </Typography>
              )}
              {priceInfo?.total_alt && (
                <Typography variant="body2" color="text.secondary">
                  ({formatCurrency(priceInfo.total_alt, currencyCode, i18n.locale)})
                </Typography>
              )}
            </Stack>
            <Button
              onClick={() => {
                onClose();
                onSelect(campsite);
              }}
              size="large"
              variant="contained"
            >
              <Trans>Select</Trans>
            </Button>
          </Stack>
        ) : showAlternativeDates ? (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "start", md: "flex-end" }}
            sx={{ cursor: "pointer", width: "100%" }}
            p={{ xs: 2, md: 0 }}
            spacing={2}
            onClick={() => {
              onClose();
              onShowAlternativeDates?.();
            }}
          >
            <Typography variant="h4" color="primary" fontWeight={600}>
              <Trans>View alternative availability</Trans>
            </Typography>
            <ChevronRight
              color="primary"
              fontSize="small"
              sx={{ display: { xs: "block", md: "none" } }}
            />
          </Stack>
        ) : (
          <Typography variant="body1" color="error" sx={{ p: { xs: 2, md: 0 } }}>
            <Trans>This campsite is not available for the selected dates.</Trans>
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  );
}
